import { v4 as uuidv4 } from 'uuid';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { getCardPaymentsForIntegration } from 'reducers/Payments';
import { Awaited, isEmpty } from 'utils';
import { CardPaymentHooks, PaymentObj } from 'paymentIntegrations/types';
import { withWaitingForTerminal } from 'paymentIntegrations';
import { getClientCode } from 'reducers/Login';
import { getCafaEntry } from 'reducers/cafaConfigs';
import { INTEGRATION_TYPES, INTEGRATIONS } from 'constants/CAFA';
import { semanticVersion } from 'external_data';
import { RootState } from 'reducers';

import * as triposAPI from './requests/triposRequests';
import { PaymentResponse } from './requests/types';
import { withResponseErrors, handleErrors } from './errors';
import {
  getPatchScriptReceipt,
  convertReceiptsToCopies,
  printReceipts,
} from './receipt';

type Mode = 'sale' | 'refund' | 'void';
type HandleSuccessParams = {
  payment: PaymentObj;
  response: PaymentResponse;
  mode?: Mode;
} & Pick<
  CardPaymentHooks,
  'beforeDocSave' | 'beforeDocDelete' | 'updateMessage' | 'displaySuccess'
>;

type MakeRequestParams<
  RequestName extends
    | 'requestSale'
    | 'requestReversal'
    | 'requestReturn'
    | 'requestVoid',
  Response extends Awaited<ReturnType<typeof triposAPI[RequestName]>>
> = {
  requestName: RequestName;
  doRequest: () => Promise<Response>;
  laneId: string;
  mode: Mode;
  payment: PaymentObj;
} & Pick<
  CardPaymentHooks,
  | 'beforeDocSave'
  | 'beforeDocDelete'
  | 'updateMessage'
  | 'enableButtons'
  | 'displaySuccess'
  | 'displayError'
>;

const handleSuccess = ({
  payment,
  response,
  beforeDocSave,
  beforeDocDelete,
  updateMessage,
  displaySuccess,
  mode = 'sale',
}: HandleSuccessParams) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
) => {
  if (response.transactionStatus.indexOf('Approved') >= 0) {
    updateMessage(`Transaction successful!`);
    displaySuccess(`Transaction successful!`);
    if (mode === 'sale' || mode === 'refund') {
      const receiptData = getPatchScriptReceipt(response);
      const copyReceiptData = convertReceiptsToCopies(receiptData);
      await beforeDocSave({
        key: payment.key,
        paid: true,
        type: 'CARD',
        amount:
          mode === 'refund'
            ? `-${Number(response.approvedAmount).toFixed(2)}`
            : Number(response.approvedAmount).toFixed(2),
        cardType: response.cardType,
        cardHolder: response.cardHolder,
        cardNumber: response.cardNumber,
        signature: response.signature || null,
        referenceNumber: response.referenceNumber,
        attributes: {
          applicationLabel: response.applicationLabel,
          aid: response.aid,
          authCode: response.authCode,
          paymentType: response.paymentType,
          dateTime: response.dateTime,
          merchantId: response.merchantId,
          referenceNumber: response.referenceNumber,
        },
        terminalResponse: {
          originalResponse: response,
          brazilMeta: {
            ...copyReceiptData,
            brazilVersion: semanticVersion,
            integrationName: INTEGRATIONS.payment.tripos,
          },
        },
      });
      await dispatch(printReceipts(receiptData));
    } else if (mode === 'void') {
      beforeDocDelete(payment.key);
    }
  }
};

const makeRequest = <
  RequestName extends
    | 'requestSale'
    | 'requestReversal'
    | 'requestReturn'
    | 'requestVoid',
  Response extends Awaited<ReturnType<typeof triposAPI[RequestName]>>
>({
  requestName,
  doRequest,
  laneId,
  mode,
  payment,
  beforeDocSave,
  beforeDocDelete,
  updateMessage,
  displaySuccess,
}: MakeRequestParams<RequestName, Response>) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
) => {
  updateMessage(`Processing ${mode} of ${payment.amount}`);
  return withResponseErrors({
    requestName,
    doRequest,
    laneId,
  }).then(async res => {
    await dispatch(
      handleSuccess({
        payment,
        response: res.data,
        beforeDocSave,
        beforeDocDelete,
        updateMessage,
        displaySuccess,
        mode,
      }),
    );
    return res;
  });
};

function createPayload(
  payment: PaymentObj & { original?: PaymentObj },
  configuration: { laneId: string },
  hooks: CardPaymentHooks,
) {
  return async (
    _dispatch: ThunkDispatch<RootState, unknown, Action>,
    getState: () => RootState,
  ) => {
    const {
      beforeDocSave,
      beforeDocDelete,
      updateMessage,
      enableButtons,
      displaySuccess,
      displayError,
    } = hooks;
    const clientCode = getClientCode(getState());
    const { amount, shouldProcess, paid, attributes = {} } = payment;
    const { referenceNumber, paymentType: curPaymentType } = attributes;

    function getMode(): Mode {
      if (Number(amount) < 0 && !paid) {
        return 'refund';
      }
      if (paid && shouldProcess) {
        return 'void';
      }
      return 'sale';
    }
    const mode = getMode();
    const paymentType = (curPaymentType ?? '').toLowerCase();

    function getRequestName():
      | 'requestSale'
      | 'requestReversal'
      | 'requestReturn'
      | 'requestVoid' {
      if (mode === 'refund') {
        // Always perform un-referenced refund since it is allowed to void it
        return 'requestReturn';
      }
      if (mode === 'void') {
        if (referenceNumber && paymentType.toLowerCase() === 'credit') {
          return 'requestVoid';
        }
        if (
          paymentType.toLowerCase() === 'credit' ||
          paymentType.toLowerCase() === 'debit'
        ) {
          return 'requestReversal';
        }
        return 'requestReturn';
      }
      return 'requestSale';
    }

    const requestName = getRequestName();
    return {
      laneId: configuration.laneId,
      requestName,
      doRequest: () => {
        if (requestName === 'requestSale') {
          return triposAPI[requestName]({
            requestID: uuidv4(),
            clientCode,
            amount: `${amount}`,
            configuration,
          });
        }
        if (requestName === 'requestReturn') {
          return triposAPI[requestName]({
            amount: `${Number(-amount)}`,
            configuration,
          });
        }
        if (requestName === 'requestVoid') {
          return triposAPI[requestName]({
            referenceNumber,
          });
        }
        // requestReversal
        return triposAPI[requestName]({
          requestID: uuidv4(),
          clientCode,
          amount: `${amount}`,
          referenceNumber,
          paymentType,
          configuration,
        });
      },
      payment,
      beforeDocSave,
      beforeDocDelete,
      updateMessage,
      enableButtons,
      displaySuccess,
      displayError,
      mode,
    };
  };
}

export default (params: CardPaymentHooks) => async (
  dispatch: ThunkDispatch<RootState, unknown, Action>,
  getState: () => RootState,
) => {
  const { updateMessage, displayError, rejectPayments, enableButtons } = params;

  const cardPayments = getCardPaymentsForIntegration('triPOS')(getState());
  const shouldVoid = cardPayments.every(cp => cp.paid && cp.shouldProcess);

  const { value: config } =
    getCafaEntry<
      typeof INTEGRATION_TYPES.payment,
      typeof INTEGRATIONS.payment.tripos,
      { LaneId: number }
    >(
      INTEGRATIONS[INTEGRATION_TYPES.payment].tripos,
      INTEGRATION_TYPES.payment,
    )(getState()) || {};

  if (!config || isEmpty(config)) {
    displayError('Tripos not configured.');
    rejectPayments();
    return { data: [], errors: ['Tripos not configured.'] };
  }
  const configuration = {
    laneId: `${config.LaneId}`,
  };
  return cardPayments
    .filter(p => (shouldVoid ? p.paid : !p.paid))
    .reduce((prev, cur) => {
      return prev.then(async ({ data, errors }) => {
        if (!errors.length) {
          await dispatch(
            withWaitingForTerminal(async () => {
              try {
                const payload = await dispatch(
                  createPayload(cur, configuration, params),
                );
                const response = await dispatch(makeRequest(payload));
                if (response.data) {
                  data.push(response.data);
                }
              } catch (err) {
                handleErrors({
                  err,
                  displayError,
                  updateMessage,
                  enableButtons,
                });
                errors.push(err.message);
              }
            }),
          );
        }
        return { data, errors };
      });
    }, Promise.resolve({ data: [], errors: [] }));
};
